import React, { useState, useRef, useEffect, ReactNode } from 'react'
import { useSpring, animated } from '@react-spring/web'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FullWidthCell } from './styles/VersatileGrid.styles'
import {
  ListInVersatileGridStyle,
  ExpandButtonKeeper,
  ExpandButton,
  Ellipsis,
} from './styles/VersatileGridUtils.styles'

// TypeScript interfaces
interface ExpansiveListInVersatileGridProps {
  children: ReactNode
  initialVisibleItems?: number
}

interface SmoothFullWidthCellProps {
  children: ReactNode
  columns: number
  isLastRow?: boolean
}

export const ExpansiveListInVersatileGrid: React.FC<
  ExpansiveListInVersatileGridProps
> = ({ children, initialVisibleItems = 2 }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const childArray = React.Children.toArray(children)
  // This line ensures the button complex only shows when there are more items than initialVisibleItems
  const hasMoreItems = childArray.length > initialVisibleItems
  const contentRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<ResizeObserver | null>(null)

  // Function to measure total height including margins
  const measureHeight = (): number => {
    const element = contentRef.current

    if (!element) return 0

    let totalHeight = element.getBoundingClientRect().height

    Array.from(element.children).forEach((child) => {
      const style = window.getComputedStyle(child)

      totalHeight +=
        parseFloat(style.marginTop) + parseFloat(style.marginBottom)
    })

    return totalHeight
  }

  // Spring animation configuration
  const [{ height }, api] = useSpring(() => ({
    from: { height: 0 },
    config: { tension: 200, friction: 20 },
  }))

  useEffect(() => {
    if (!contentRef.current) return

    // Update height when content changes
    const updateHeight = () => {
      const newHeight = measureHeight()

      api.start({ height: newHeight })
    }

    // Setup ResizeObserver to watch for content size changes
    observerRef.current = new ResizeObserver(() => {
      window.requestAnimationFrame(updateHeight)
    })

    // Observe the content element and all its children
    observerRef.current.observe(contentRef.current)
    Array.from(contentRef.current.getElementsByTagName('*')).forEach(
      (element) => {
        observerRef.current?.observe(element)
      },
    )

    // Initial height measurement
    updateHeight()

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [api, isExpanded])
  return (
    <div>
      {/* @ts-ignore - animated.div typing issue with react-spring, functionality preserved */}
      <animated.div
        style={{
          overflow: 'hidden',
          height: height,
        }}
      >
        <div css={ListInVersatileGridStyle} ref={contentRef}>
          {isExpanded ? childArray : childArray.slice(0, initialVisibleItems)}
        </div>
      </animated.div>

      {/* Button complex only renders if hasMoreItems is true */}
      {hasMoreItems && (
        <ExpandButtonKeeper>
          {!isExpanded && <Ellipsis>...</Ellipsis>}
          <ExpandButton onClick={() => setIsExpanded(!isExpanded)}>
            <FontAwesomeIcon
              style={{ paddingRight: '0.2rem', paddingLeft: '0.0rem' }}
              icon={isExpanded ? faChevronUp : faChevronDown}
            />
            {isExpanded ? 'Hide' : 'Show more'}
          </ExpandButton>
        </ExpandButtonKeeper>
      )}
    </div>
  )
}

/* SmoothFullWidthCell Component
 * Wraps FullWidthCell with height animation capabilities
 * Uses ResizeObserver to detect content changes and animate smoothly
 */
export const SmoothFullWidthCell: React.FC<SmoothFullWidthCellProps> = ({
  children,
  columns,
  isLastRow,
}) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<ResizeObserver | null>(null)
  const [, setTargetHeight] = useState<number>(0)

  // Spring animation for height changes
  const [{ height }, api] = useSpring(() => ({
    from: { height: 0 },
    to: { height: 0 },
    config: { tension: 200, friction: 20 },
  }))

  // Add state to track if content is effectively empty
  const [isEffectivelyEmpty, setIsEffectivelyEmpty] = useState<boolean>(false)
  const MIN_CONTENT_HEIGHT = 2 // Minimum height threshold in pixels

  // Function to measure total height including margins
  const measureHeight = (): number => {
    const element = contentRef.current

    if (!element) return 0

    // Get all direct children
    const children = Array.from(element.children)

    if (children.length === 0) return 0

    // Calculate total height including margins
    let totalHeight = 0

    children.forEach((child) => {
      const style = window.getComputedStyle(child)
      const marginTop = parseFloat(style.marginTop)
      const marginBottom = parseFloat(style.marginBottom)
      const elementHeight = child.getBoundingClientRect().height

      totalHeight += elementHeight + marginTop + marginBottom
    })

    return totalHeight
  }

  useEffect(() => {
    if (!contentRef.current) return

    // Update height when content changes
    const updateHeight = () => {
      const newHeight = measureHeight()

      setTargetHeight(newHeight)
      setIsEffectivelyEmpty(newHeight <= MIN_CONTENT_HEIGHT)

      // Animate to the new height
      api.start({
        height: newHeight,
        immediate: false,
      })
    }

    // Setup ResizeObserver
    observerRef.current = new ResizeObserver(() => {
      window.requestAnimationFrame(updateHeight)
    })

    // Observe the content and all its children
    const element = contentRef.current

    if (element) {
      observerRef.current.observe(element)
      Array.from(element.getElementsByTagName('*')).forEach((child) => {
        observerRef.current?.observe(child)
      })
    }

    // Initial height measurement
    updateHeight()

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [api, children]) // Added children as dependency to react to presence/absence

  return (
    <FullWidthCell
      columns={columns}
      isLastRow={isLastRow}
      hideBottomBorder={isEffectivelyEmpty}
    >
      {/* @ts-ignore - animated.div typing issue with react-spring, functionality preserved */}
      <animated.div style={{ height, overflow: 'hidden' }}>
        <div ref={contentRef}>{children}</div>
      </animated.div>
    </FullWidthCell>
  )
}
